












































































































































































































































































import Vue from "vue";
import { computed, onBeforeUnmount, onMounted, ref } from "@vue/composition-api";
import Modal from "@/components/root/modal/Modal.vue";
import { ModalConfig } from "@/components/root/modal";
import { pxToRem } from "@/common/dom.functions";
import GaIcon from "../../card-items/GaIcon.vue";
import { useGenAiPalette } from "../../data/design";
import GaSensitivityBadge from "./GaSensitivityBadge.vue";
import { is_genai_app } from "@/common/ai-agent";
import {
  EventFile,
  EventView,
  SENSITIVITY_SHORT,
  SEVERITY_LABEL,
  department_line,
  overview_narrative,
  risk_situation,
} from "./event-view";

/**
 * The prototype's "AI Interaction" tab is a timeline of the whole Copilot
 * conversation an activity belongs to. Nothing in the live event carries that
 * conversation, so the tab is left out rather than drawn with one made-up
 * step; the files and the people it names are live, so those tabs are kept.
 */
export default Vue.extend({
  components: { Modal, GaIcon, GaSensitivityBadge },
  props: ["modalData"],
  setup(props, { emit }) {
    const T = useGenAiPalette();
    const e = computed(() => (props.modalData as { event: EventView }).event);
    const active = ref("overview");

    const config: ModalConfig = {
      title: "",
      desc: "",
      blank: true,
      transparentBg: true,
      backdropDismiss: true,
      width: pxToRem(Math.min(980, innerWidth * 0.92)) + "rem",
      // sized by its content, as the prototype's dialog is (max 90vh)
      height: "auto",
    };

    const tabs = computed(() => [
      { id: "overview", label: "Overview", icon: "chart-no-axes-column" },
      { id: "content", label: `Content (${e.value.files.length})`, icon: "file-text" },
      {
        id: "entities",
        label: `Entities (${e.value.users.length + (e.value.raw.exposedBy ? 1 : 0)})`,
        icon: "user",
      },
    ]);

    const narrative = computed(() => overview_narrative(e.value));

    const content_note = computed(() => {
      const m = e.value.match;
      if (!m || m.level !== "event" || e.value.files.length < 2) return "";
      return `Every file in this event. The list does not identify which of them matched ${m.relation}.`;
    });
    const is_matched_file = (f: EventFile) =>
      !!e.value.match && e.value.match.level === "item" && e.value.match.file_id === f.id;
    const situation = computed(() => risk_situation(e.value));
    const severity_label = computed(() => SEVERITY_LABEL[e.value.risk]);
    const sensitivity_label = computed(() => SENSITIVITY_SHORT[e.value.sensitivity]);
    const files_line = computed(() => {
      const names = e.value.content.files;
      return names.slice(0, 3).join(", ") + (names.length > 3 ? ` +${names.length - 3} more` : "");
    });
    const app_label = computed(() => e.value.app || e.value.agent.name);
    const app_initial = computed(() => (app_label.value || "C").charAt(0).toUpperCase());
    const app_is_genai = computed(() => is_genai_app(app_label.value));

    const people = computed(() => {
      const by = e.value.raw.exposedBy;
      return [
        {
          title: "Exposed By",
          people: by
            ? [
                {
                  key: "by",
                  name: by.name || by.email || "Unknown",
                  email: by.name ? by.email : "",
                  department: department_line(by),
                  action: { type: "investigate_sender" },
                },
              ]
            : [],
        },
        {
          title: "Exposed To",
          people: e.value.users.map((u, i) => ({
            key: (u.id || "") + i,
            name: u.name || u.email || "Unknown",
            email: u.name ? u.email : "",
            department: department_line(u),
            action: u.id ? { type: "investigate_person", id: u.id } : null,
          })),
        },
      ].filter((g) => g.people.length);
    });

    const on_key = (ev: KeyboardEvent) => {
      if (ev.key === "Escape") emit("cancel");
    };
    onMounted(() => document.addEventListener("keydown", on_key));
    onBeforeUnmount(() => document.removeEventListener("keydown", on_key));

    const row = computed(() => ({
      padding: "14px 20px",
      gap: "10px",
      borderBottom: "1px solid " + T.value.ev.card.divider,
    }));
    const cell = { padding: "16px 12px", verticalAlign: "top", fontSize: "12px" };
    const content_head = computed(() => ({
      padding: "8px 12px",
      fontSize: "12px",
      fontWeight: 600,
      color: T.value.ev.strong,
      borderBottom: "1px solid " + T.value.ev.card.border,
    }));

    return {
      T,
      e,
      active,
      config,
      tabs,
      narrative,
      content_note,
      is_matched_file,
      situation,
      severity_label,
      sensitivity_label,
      files_line,
      app_label,
      app_initial,
      app_is_genai,
      people,
      row,
      cell,
      content_head,
      content_heads: [
        { label: "Name", width: "28%" },
        { label: "Information Type", width: "28%" },
        { label: "Sensitivity", width: "16%", center: true },
        { label: "Personal Identifiers", width: "28%" },
      ],
    };
  },
});
